/**
 * `useMessageComposePort` — factory hook for the message-compose port (027),
 * now REAL-backed (050 US1): a submit goes to the live `memberMessaging.
 * submitMessage` tRPC procedure, which creates a real case in the Integration
 * Service (mock-asianhallinta in dev). The member's own message then appears in
 * the single Viestit conversation list through the real read — no device-local
 * echo.
 *
 * The former demo seam (a `SentListEchoingMessageComposePort` / the
 * `DemoMessageComposeAdapter` recorder that wrote a device-local sent-record so
 * the send showed under a "Lähetetyt" tab, 030 deviation A) is RETIRED (050
 * US2): the send is real and the sent message is read back from the back office,
 * so no browser-local copy is kept (no shadow copy, P1/P7; the device-local
 * `localStorage` store is deleted).
 *
 * The `options.port` seam lets browser / wizard tests inject a fixture compose
 * port (`DemoMessageComposeAdapter` — its topic tree + configurable submit
 * result); production omits it and uses the real `TrpcMessageComposeAdapter`.
 *
 * Honest degrade: if the `memberMessaging` namespace is not mounted (a demo/
 * standalone composition with no Integration Service), the adapter refuses at
 * submit with a clear error rather than fabricating success (P7).
 */

import { useState } from 'react';

import type { MessageComposePort } from '../member-messaging/compose/MessageComposePort.js';
import { TrpcMessageComposeAdapter } from '../member-messaging/compose/TrpcMessageComposeAdapter.js';
import { createWebTrpcClient } from '../trpc/client.js';

export interface UseMessageComposePortOptions {
  /**
   * Explicit composition seam for browser tests and wizard harnesses.
   * Production omits it and uses {@link TrpcMessageComposeAdapter}.
   */
  readonly port?: MessageComposePort;
}

export function useMessageComposePort(
  options: UseMessageComposePortOptions = {},
): MessageComposePort {
  const [port] = useState<MessageComposePort>(
    () => options.port ?? new TrpcMessageComposeAdapter(createWebTrpcClient()),
  );
  return port;
}
